import React, { useEffect, useRef, useState } from 'react';
import { encodeBase64, decodeBase64 } from '../base64';

const owner = process.env.REACT_APP_GITHUB_OWNER;
const repo = process.env.REACT_APP_GITHUB_REPO;
const branch = process.env.REACT_APP_GITHUB_BRANCH || 'main';
const token = process.env.REACT_APP_GITHUB_TOKEN;

async function getFile(path) {
  const res = await fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}?ref=${branch}`, {
    headers: token ? { Authorization: `token ${token}` } : {}
  });
  if (!res.ok) return null;
  return res.json();
}

async function updateFile(path, content, message, sha) {
  return fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}`, {
    method: 'PUT',
    headers: {
      Authorization: `token ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ message, content, branch, sha })
  });
}

function FullDescriptionForm({ event, onClose, onSaved }) {
  const [fullDescription, setFullDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    setFullDescription(event.fullDescription || '');
  }, [event]);


  const formatUrl = (url) => {
    const trimmed = url.trim();
    if (!trimmed) return '';
    if (/^(https?:\/\/|mailto:)/i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleAddLink = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    if (start === end) {
      setError('Highlight text before adding a link');
      textarea.focus();
      return;
    }

    const selectedText = fullDescription.slice(start, end);
    const url = window.prompt('Enter link URL');
    if (url === null) {
      textarea.focus();
      return;
    }

    const formattedUrl = formatUrl(url);
    if (!formattedUrl) {
      setError('Enter a link URL');
      textarea.focus();
      return;
    }

    setError('');
    const linkedText = `[${selectedText}](${formattedUrl})`;
    const nextDescription = `${fullDescription.slice(0, start)}${linkedText}${fullDescription.slice(end)}`;
    setFullDescription(nextDescription);

    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + linkedText.length);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const file = await getFile('events.json');
    if (!file) {
      setError('Could not load events.json');
      setSaving(false);
      return;
    }

    try {
      const events = JSON.parse(decodeBase64(file.content));
      const idx = events.findIndex((evt) => evt.id === event.id);
      if (idx < 0) {
        setError('Event not found');
        setSaving(false);
        return;
      }

      const evt = { ...events[idx] };
      const trimmed = fullDescription.trim();
      if (trimmed) {
        evt.fullDescription = trimmed;
      } else {
        delete evt.fullDescription;
      }

      events[idx] = evt;
      const res = await updateFile(
        'events.json',
        encodeBase64(JSON.stringify(events, null, 2)),
        `Update full description ${event.id}`,
        file.sha
      );
      if (!res.ok) {
        setError('Could not save full description');
        setSaving(false);
        return;
      }

      setSaving(false);
      onSaved(evt);
      onClose();
    } catch (err) {
      console.error(err);
      setError('Could not save full description');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-20">
      <form className="bg-white p-6 flex flex-col gap-3 w-[90vw] max-w-3xl max-h-[90vh]" onSubmit={handleSubmit}>
        <h2 className="text-xl mb-2">Full Description</h2>
        <div className="flex items-center justify-between gap-2">
          <button type="button" className="underline" onClick={handleAddLink}>
            Add Link
          </button>
          <span className="text-sm text-gray-600">Highlight text, then click Add Link</span>
        </div>
        <textarea
          ref={textareaRef}
          className="border p-3 min-h-[50vh] resize-y"
          rows="18"
          value={fullDescription}
          onChange={(e) => setFullDescription(e.target.value)}
          placeholder="Full description"
        />
        {error && <div className="text-red-500 text-sm">{error}</div>}
        <div className="flex gap-2 mt-2">
          <button type="submit" className="bg-black text-white p-2" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          <button type="button" className="p-2" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default FullDescriptionForm;
